<template>
  <SideSheet position="left" :is-open="customer && isOpen" @close="close">
    <NavigationPanelWrapper>
      <NavigationPanel v-slot="{ setPanel }">
        <div class="full-height">
          <FullHeightPanel>
            <NavBar
              slot="top"
              background="transparent"
              class="navbar-container"
            >
              <div slot="left">
                <CloseButton :colored-background="false" @click="close" />
              </div>
              <div slot="center" :class="computedColor">Menu</div>
            </NavBar>
            <ul slot="center" class="main-menu">
              <li>
                <MenuItem
                  chevron
                  data-testid="my-account-item"
                  @click="
                    setPanel(1);
                    trackAmplitudeEvents.trackMyAccountClick();
                  "
                >
                  <span class="menu_my_account_title">
                    {{ getString('dash_menu_my_account_title') }}
                  </span>
                </MenuItem>
              </li>
              <li v-if="customer.isRegisterVerified">
                <MenuItem
                  chevron
                  data-testid="my-investments-item"
                  @click="
                    setPanel(2);
                    trackAmplitudeEvents.trackMyInvestmentsClick();
                  "
                >
                  {{ getString('dash_menu_investments_title') }}
                </MenuItem>
              </li>
              <MenuItem
                v-if="showPortabilityExit"
                @click="goToPortabilityExitRoute"
              >
                Portabilidade de investimentos
              </MenuItem>
              <li v-if="canShowCostsItem">
                <MenuItem data-testid="costs-item" @click="goToCosts">
                  {{ getString('cashback_overview_screen_title') }}
                </MenuItem>
              </li>
              <li v-if="showPensionMenu">
                <MenuItem data-testid="pension-link" @click="goToPension">
                  {{ getString('investment_summary_item_privatepensionfunds') }}
                </MenuItem>
              </li>
              <li v-if="showAvailableProducts">
                <MenuItem @click="goToProducts">Produtos disponíveis</MenuItem>
              </li>
              <li>
                <MenuItem
                  chevron
                  data-testid="report-button"
                  @click="
                    setPanel(5);
                    trackAmplitudeEvents.trackReportMenuClick();
                  "
                >
                  Relatórios
                </MenuItem>
              </li>
              <li v-if="showFAQPage">
                <MenuItem @click="goToHelpCenter">Me Ajuda</MenuItem>
              </li>
              <li v-if="hasSwitchAccount()">
                <MenuItem chevron @click="openSwitchAccountModal">
                  Entrar como parceiro
                </MenuItem>
              </li>
              <li v-if="customer.isRegisterVerified">
                <MenuItem
                  chevron
                  data-testid="security-button"
                  @click="
                    setPanel(4);
                    trackAmplitudeEvents.trackSecurityLoad();
                  "
                >
                  Segurança
                </MenuItem>
              </li>
              <li>
                <Row :has-line="false" :class="computedClasses">
                  <ToggleSwitch
                    slot="top-left"
                    data-testid="switch-dark-mode"
                    class="toggle__dark-mode"
                    hide-divider
                    :value="isDarkMode"
                    @input="switchDarkMode"
                  >
                    <BaseText size="lg">Tema escuro</BaseText>
                  </ToggleSwitch>
                </Row>
              </li>
            </ul>
            <ul slot="bottom">
              <li v-if="customer.name && customer.email">
                <Row :has-line="false" :class="computedClasses">
                  <div slot="icon-right">
                    <RoundedIcon
                      style="right: -4px"
                      :text="customer.name[0]"
                      :has-hover="true"
                      :main-color="NebraskaColors.elementPrimary"
                      :text-color="NebraskaColors.backgroundPrimary"
                      :hover-color="NebraskaColors.backgroundPrimary"
                      :hover-text-color="NebraskaColors.elementPrimary"
                    />
                  </div>
                  <div slot="top-left" class="text-wrap">
                    {{ customer.name }}
                  </div>
                  <div slot="bottom-left" class="text-wrap">
                    {{ customer.email }}
                  </div>
                </Row>
              </li>
              <li>
                <button class="menu-item" @click="logoutUser">
                  <Row :has-line="true" :class="computedClasses">
                    <div slot="top-left">
                      <span class="text-p5-semibold"> Sair </span>
                    </div>
                  </Row>
                </button>
              </li>
              <li v-show="appVersion">
                <BaseText as="span" size="xs" class="version-item">
                  {{ appVersion }}
                </BaseText>
              </li>
            </ul>
          </FullHeightPanel>
        </div>
      </NavigationPanel>

      <NavigationPanel v-slot="{ setPanel }">
        <div class="full-height">
          <FullHeightPanel>
            <NavBar
              slot="top"
              background="transparent"
              class="navbar-container"
            >
              <div slot="left">
                <BackButton @click="setPanel(0)" />
              </div>
              <div slot="center" :class="computedColor">
                {{ myAccountMenu.title }}
              </div>
            </NavBar>
            <ul slot="center" class="sub-menu-2">
              <li
                v-for="(row, index) in myAccountMenu.accountInformation"
                :key="row.title"
                :class="
                  index !== myAccountMenu.accountInformation.length - 1 ||
                  showServicing
                    ? 'account-info account-info-divider'
                    : 'account-info'
                "
              >
                <div class="account-info-title">
                  <span class="text-p-3">{{ row.title }}</span>
                  <CopyButton :value="row.description" />
                </div>
                <div class="account-info-desc" :has-line="false">
                  <div v-if="!row.infos" class="text-p-4">
                    {{ row.description }}
                  </div>
                  <div v-else>
                    <div class="text-p-4">
                      {{ row.infos.name }}
                    </div>
                    <span class="account-info-desc-email text-p-4">
                      {{ row.infos.email }}
                    </span>
                    <span class="text-p-4">{{ row.infos.phone }}</span>
                  </div>
                </div>
              </li>
              <li
                v-if="showServicing"
                class="account-info servicing-info"
                @click="goToServicing"
              >
                <div class="account-info-title">
                  <span class="text-p-3">Seu atendimento</span>
                  <BaseIcon
                    class="servicing-info__chevron"
                    height="22"
                    icon="ic_chevron_right"
                    width="22"
                  />
                </div>
                <div class="account-info-desc">
                  <div class="text-p-4">{{ advisorName }}</div>
                </div>
              </li>
              <li v-for="row in myAccountMenu.rows" :key="row.title">
                <MenuItem @click="handleMyAccountActions(row.action)">
                  <span class="text-p-3 wrn-flex">
                    {{ row.title }}
                  </span>
                  <template v-if="row.chip" #right>
                    <ChipStatus :label="row.chip" status="alert" />
                  </template>
                </MenuItem>
              </li>
            </ul>
            <ButtonContainer slot="bottom">
              <template #button-secondary>
                <ButtonSecondary
                  class="text-p5-semibold"
                  :label="myAccountMenu.button.label"
                  @click="handleMyAccountActions(myAccountMenu.button.action)"
                />
              </template>
            </ButtonContainer>
          </FullHeightPanel>
        </div>
      </NavigationPanel>

      <NavigationPanel v-slot="{ setPanel }">
        <div class="full-height">
          <FullHeightPanel>
            <NavBar
              slot="top"
              background="transparent"
              class="navbar-container"
            >
              <div slot="left">
                <BackButton
                  data-testid="investment-back-button"
                  @click="
                    setPanel(0);
                    trackAmplitudeEvents.trackMyInvestmentsBack();
                  "
                />
              </div>
              <div slot="center" :class="computedColor">
                Extratos e relatórios
              </div>
            </NavBar>
            <ul slot="center" class="sub-menu-3">
              <li>
                <MenuItem
                  data-testid="trade-notes-button"
                  @click="goToTradeNotesPage"
                >
                  Notas de negociação
                </MenuItem>
              </li>
              <li>
                <MenuItem
                  data-testid="income-statement-download"
                  @click="goToIncomeReportView"
                >
                  Informe de rendimentos
                </MenuItem>
              </li>
              <li>
                <MenuItem
                  data-testid="consolidated-statement-download"
                  @click="
                    goToStatementDownload(StatementType.ConsolidatedStatement)
                  "
                >
                  Extrato consolidado
                </MenuItem>
              </li>
              <li>
                <MenuItem
                  data-testid="fixed-income-statement-download"
                  @click="
                    goToStatementDownload(StatementType.FixedIncomeStatement)
                  "
                >
                  Extrato de renda fixa
                </MenuItem>
              </li>
              <li>
                <MenuItem
                  data-testid="quarterly-statement-download"
                  @click="
                    goToStatementDownload(StatementType.QuarterlyCostsStatement)
                  "
                >
                  Extrato de custos
                </MenuItem>
              </li>
              <li>
                <MenuItem
                  data-testid="allocation-performance-statement-download"
                  @click="goToStatementDownload(StatementType.InvestmentReport)"
                >
                  Relatório de investimentos
                </MenuItem>
              </li>
            </ul>
          </FullHeightPanel>
        </div>
      </NavigationPanel>

      <NavigationPanel v-slot="{ setPanel }">
        <div class="full-height">
          <FullHeightPanel>
            <NavBar
              slot="top"
              background="transparent"
              class="navbar-container"
            >
              <div slot="left">
                <BackButton @click="setPanel(0)" />
              </div>
              <div slot="center" :class="computedColor">Dúvidas</div>
            </NavBar>
            <ul slot="center" class="sub-menu-4">
              <li>
                <a href="mailto:meajuda@warrenbrasil.com">
                  <Row :has-line="false" :class="computedClasses">
                    <div slot="icon-right">
                      <BaseIcon icon="ic_mail" />
                    </div>
                    <div slot="top-left">{{ getString('contact_email') }}</div>
                  </Row>
                </a>
              </li>
              <li>
                <button class="menu-item" @click="openZendeskChat">
                  <Row :has-line="false" :class="computedClasses">
                    <div slot="icon-right">
                      <BaseIcon icon="ic_chat" has-hover />
                    </div>
                    <div slot="top-left">
                      {{ getString('contact_chat_title') }}
                    </div>
                  </Row>
                </button>
              </li>
            </ul>
          </FullHeightPanel>
        </div>
      </NavigationPanel>

      <NavigationPanel v-slot="{ setPanel }">
        <div class="full-height">
          <FullHeightPanel>
            <NavBar
              slot="top"
              background="transparent"
              class="navbar-container"
            >
              <div slot="left">
                <BackButton
                  data-testid="back-security-button"
                  @click="
                    setPanel(0);
                    trackAmplitudeEvents.trackSecurityBackClick();
                  "
                />
              </div>
              <div slot="center" :class="computedColor">Segurança</div>
            </NavBar>
            <ul slot="center" class="sub-menu-2">
              <li>
                <MenuItem
                  :chevron="false"
                  data-testid="mfa-button"
                  @click="goToMFAPage"
                >
                  Verificação em duas etapas
                </MenuItem>
              </li>
            </ul>
          </FullHeightPanel>
        </div>
      </NavigationPanel>

      <NavigationPanel v-slot="{ setPanel }">
        <div class="full-height">
          <FullHeightPanel>
            <NavBar
              slot="top"
              background="transparent"
              class="navbar-container"
            >
              <div slot="left">
                <BackButton
                  data-testid="back-report-button"
                  @click="setPanel(0)"
                />
              </div>
              <div slot="center" :class="computedColor">Relatórios</div>
            </NavBar>
            <ul slot="center" class="sub-menu-2">
              <li>
                <MenuItem
                  :chevron="false"
                  data-testid="allocation-button"
                  @click="goToAllocationPage"
                >
                  Alocação
                </MenuItem>
              </li>
              <li>
                <MenuItem
                  :chevron="false"
                  data-testid="analysis-button"
                  @click="goToAnalysisPage"
                >
                  Análise
                </MenuItem>
              </li>
              <li>
                <MenuItem
                  :chevron="false"
                  data-testid="asset-button"
                  @click="goToAssetPage"
                >
                  Asset Management
                </MenuItem>
              </li>
            </ul>
          </FullHeightPanel>
        </div>
      </NavigationPanel>
    </NavigationPanelWrapper>

    <DeleteAccount
      v-if="deleteAccountModal"
      @close="deleteAccountModal = false"
    />

    <SwitchAccountModal
      v-if="userB2b"
      ref="switchAccountModal"
      :user="userB2b"
    />

    <Notification group="wrn-notification-menu" />
  </SideSheet>
</template>

<script lang="ts">
import { Component, Prop, Mixins, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import {
  Row,
  ToggleSwitch,
  BaseIcon,
  RoundedIcon,
  Tag,
  BaseText,
  ChipStatus,
  ButtonSecondary,
  ButtonContainer,
  CopyButton
} from '@warrenbrasil/nebraska-web';
import { SideSheet } from '@/modules/common/components/SideSheet';
import { NavBar } from '@/modules/common/components/__deprecated__/NavBar';
import {
  NavigationPanelWrapper,
  NavigationPanel
} from '@/modules/common/components/NavigationPanel';
import { Notification } from '@/modules/common/components/Notification';
import { FullHeightPanel } from '@/modules/common/components/FullHeightPanel';
import { CountBadge } from '@/modules/common/components/badges/CountBadge';
import { CloseButton } from '@/modules/common/components/buttons';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import { MenuItem } from './components/MenuItem';
import { DeleteAccount } from './components/DeleteAccount';
import { SwitchAccountModal } from './components/SwitchAccountModal';
import { getString } from '@/modules/common/helpers/resources';
import { ICustomer } from '@/types/models/Customer';
import { RouteMixin } from '@/modules/common/mixins/route';
import {
  IUserB2B,
  getUserB2B,
  StorageKeys
} from '@/modules/common/services/local-storage';
import {
  logoutImpersonate,
  isWarrenManaged
} from '@/modules/common/services/partner';
import { IAdvisorProfile } from '@/types/models';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import { GtmTracker } from '@/modules/common/services/analytics';
import { useDashStore } from '@/modules/common/store/DashModule';
import { zendeskLoader } from '@/modules/common/services/zendesk/zendesk-loader';
import * as trackAmplitudeEvents from './track-events';
import { StatementType } from '@/modules/investments/types/statementType';
import { IMyAccountMenu, MyAccountAction } from './types';
import { MFAModules } from '@/modules/authentication/views/SecurityView/components/MultiFactorAuthentication';
import { RegulatoryUpdateStatus } from '@/modules/common/services/http/account';
import { getMyAccountMenu } from './services/my-account';
import { getDomainAndPlatform } from '../../helpers/onboarding/getDomainAndPlatform';

const customerModule = namespace('CustomerModule');
const UIModule = namespace('UIModule');

@Component({
  components: {
    BaseText,
    BaseIcon,
    RoundedIcon,
    Row,
    CountBadge,
    SwitchAccountModal,
    Notification,
    MenuItem,
    SideSheet,
    NavigationPanelWrapper,
    NavigationPanel,
    FullHeightPanel,
    DeleteAccount,
    BackButton,
    CloseButton,
    NavBar,
    ToggleSwitch,
    Tag,
    ChipStatus,
    ButtonSecondary,
    ButtonContainer,
    CopyButton
  }
})
export default class MenuList extends Mixins(RouteMixin) {
  private readonly dashModule = useDashStore();

  @Prop({ type: Number, default: 0 })
  public countNotification!: number;

  @customerModule.Getter('getCustomerId')
  private customerId!: string;

  @customerModule.State('regulatoryUpdateStatus')
  private regulatoryUpdateStatus!: RegulatoryUpdateStatus;

  private getString = getString;
  private NebraskaColors = NebraskaColors;
  private deleteAccountModal = false;
  private userB2b = getUserB2B()!;
  readonly trackAmplitudeEvents = trackAmplitudeEvents;
  public myAccountMenu: IMyAccountMenu = {
    title: '',
    rows: [],
    button: { label: '', action: MyAccountAction.DeleteAccount }
  };

  @customerModule.Getter('getCustomer')
  private customer!: ICustomer;

  @UIModule.Getter('getIsOpenLeftSideBar')
  private isOpen!: boolean;

  @UIModule.Mutation('closeLeftSideBar')
  private close!: () => void;

  @Watch('isOpen')
  private watchMenuOpening(isOpen: boolean) {
    isOpen && trackAmplitudeEvents.trackMenuOpening();
  }

  private get showPensionMenu() {
    return (
      this.customer && this.customer.isRegisterVerified && isWarrenManaged()
    );
  }

  private get showFAQPage() {
    return FeatureFlagsModule.isEnabled(FeatureFlags.CS_CHAT);
  }

  get showAvailableProducts() {
    return (
      this.customer.isRegisterVerified &&
      this.regulatoryUpdateStatus !== RegulatoryUpdateStatus.BLOCKED
    );
  }

  readonly StatementType = StatementType;

  private async created() {
    this.myAccountMenu = await getMyAccountMenu(this.customerId);
  }

  private get trackEvent() {
    return GtmTracker.trackerWithDefaultProps({
      label: 'geral',
      category: 'menu_sidebar'
    });
  }

  public suitabilityLink() {
    const { domain } = getDomainAndPlatform();

    window.location.href = `/onboarding/${domain}/desktop/suitability/user-profile`;
  }

  private get complianceTermsLink() {
    return process.env.VUE_APP_COMPLIANCE_TERMS;
  }

  public handleMyAccountActions(action: MyAccountAction) {
    const goToChangeEmail = () => {
      const { domain } = getDomainAndPlatform();

      trackAmplitudeEvents.trackChangeEmailSubmenu();
      window.location.href = `/onboarding/${domain}/desktop/change-email`;
    };

    const goToInvestmentProfile = () => {
      this.suitabilityLink();
    };

    const goToChangePassword = () => {
      const { domain } = getDomainAndPlatform();

      trackAmplitudeEvents.trackChangePasswordSubmenu();
      window.location.href = `/onboarding/${domain}/desktop/change-password`;
    };

    const goToEditRegister = () => {
      this.goToRouteName(
        'user-account',
        trackAmplitudeEvents.trackAccountRegisterSubmenu
      );
    };

    const goToInitialRegister = () => {
      this.$router.push({
        name: 'home'
      });
    };

    const goToRegulatoryUpdate = () => {
      this.goToRouteName('regulatory-update');
    };

    const goToComplianceTerms = () => {
      trackAmplitudeEvents.trackViewTermsSubmenu();
      window.open(this.complianceTermsLink, '_blank');
    };

    const goToMfaValidationThenChangeEmail = () => {
      this.$router.push({
        name: 'security',
        query: { module: MFAModules.MENU_CHANGE_EMAIL }
      });
    };

    const goToMfaValidationThenEditRegister = () => {
      this.$router.push({
        name: 'security',
        query: { module: MFAModules.MENU_EDIT_REGISTER }
      });
    };

    const goToDeleteAccount = () => {
      this.deleteAccountModal = true;
      trackAmplitudeEvents.trackDeleteAccountSubmenu();
    };

    const actions = {
      [MyAccountAction.ChangeEmail]: goToChangeEmail,
      [MyAccountAction.ChangePassword]: goToChangePassword,
      [MyAccountAction.DeleteAccount]: goToDeleteAccount,
      [MyAccountAction.EditRegister]: goToEditRegister,
      [MyAccountAction.InitialRegister]: goToInitialRegister,
      [MyAccountAction.InvestmentProfile]: goToInvestmentProfile,
      [MyAccountAction.MfaValidationThenChangeEmail]:
        goToMfaValidationThenChangeEmail,
      [MyAccountAction.MfaValidationThenEditRegister]:
        goToMfaValidationThenEditRegister,
      [MyAccountAction.RegulatoryUpdate]: goToRegulatoryUpdate,
      [MyAccountAction.Terms]: goToComplianceTerms
    };

    actions[action]();
  }

  private get showPortabilityExit() {
    return FeatureFlagsModule.isEnabled(
      FeatureFlags.PORTABILITY_EXIT_MENU_ITEM
    );
  }

  private get showServicing() {
    return FeatureFlagsModule.isEnabled(FeatureFlags.ENABLE_ADVISOR_CHANGE);
  }

  private goToPortabilityExitRoute() {
    window.location.assign('/products/#/portability/create');
  }

  private get advisorName() {
    return this.customer?.partnerInfos?.name || '';
  }

  private goToServicing() {
    window.location.assign('/products/#/attendance');
  }

  private goToProducts() {
    this.$router.push({
      name: 'productsCatalog',
      params: {
        type: 'fixed-income'
      }
    });
  }

  private goToAllocationPage() {
    trackAmplitudeEvents.trackAllocationClick();
    window.open('https://lp.warren.com.br/warren-asset-allocation', '_blank');
  }

  private goToAnalysisPage() {
    trackAmplitudeEvents.trackAnalysisClick();
    window.open('https://lp.warren.com.br/entenda-sobre-o-mercado', '_blank');
  }

  private goToAssetPage() {
    trackAmplitudeEvents.trackAssetsManagementClick();
    window.open('https://lp.warren.com.br/conheca-nossos-fundos', '_blank');
  }

  private goToHelpCenter() {
    this.trackEvent({ action: 'click_me_ajuda' });
    this.goToRouteName('help-center');
  }

  protected goToPension() {
    this.trackEvent({ action: 'click_previdencia' });
    window.open('https://lp.warren.com.br/previdencia', '_blank');
  }

  private goToRouteName(name: string, tracker?: Function) {
    this.$router.push({ name });
    this.close();
    tracker?.();
  }

  private switchDarkMode() {
    this.dashModule.toggleDarkMode();
  }

  private get isDarkMode() {
    return this.dashModule.isDarkMode;
  }

  private openSwitchAccountModal() {
    (this.$refs as { switchAccountModal: any }).switchAccountModal.open();
  }

  // -- DADOS COMPUTADOS -- //
  private get computedClasses() {
    return ['wrn-cta-white', 'navbar-cta', 'wrn-p-x-md-1'];
  }

  private get computedColor() {
    return ['wrn-navbar-text navbar-text text-p6-semibold'];
  }

  private get computedProfileType(): string {
    if (
      this.customer &&
      this.customer.investmentProfile &&
      this.customer.investmentProfile.computedProfileType
    ) {
      return this.customer.investmentProfile.computedProfileType;
    } else {
      return '';
    }
  }

  private isCustomerPJ() {
    if (!this.customer) return;
    return !!this.customer.professionalProfile;
  }

  // -- ME AJUDA -- //
  private async openZendeskChat() {
    try {
      await zendeskLoader.openChat();
    } catch (error) {
      console.error('Erro ao abrir chat:', error);
    }
  }

  // -- LOGOUT -- //
  private logoutUser() {
    trackAmplitudeEvents.trackLogoutClick();
    this.proceedLogout();
  }

  private proceedLogout() {
    if (
      this.customer &&
      this.customer.clientType &&
      this.customer.clientType.whitelabel
    ) {
      if (this.customer.isImpersonate) {
        logoutImpersonate();
      } else {
        this.$router.push({
          name: 'signout',
          query: {
            redirectToPath: '/app/#/signin',
            preserveTokens: [
              StorageKeys.RefreshToken,
              StorageKeys.HasUsedPositionDetailsButton,
              StorageKeys.DarkMode,
              StorageKeys.PartnerDashNewsInformation,
              StorageKeys.PartnerPortfolioNewsInformation,
              StorageKeys.PartnerStatementNewsInformation,
              StorageKeys.PartnerProfileNewsInformation
            ]
          }
        });
      }
    } else {
      this.$router.push({
        name: 'signout',
        query: {
          preserveTokens: [
            StorageKeys.RefreshToken,
            StorageKeys.HasUsedPositionDetailsButton,
            StorageKeys.DarkMode,
            StorageKeys.PartnerDashNewsInformation,
            StorageKeys.PartnerPortfolioNewsInformation,
            StorageKeys.PartnerStatementNewsInformation,
            StorageKeys.PartnerProfileNewsInformation
          ]
        }
      });
    }
    if (this.isOpen) this.close();
  }

  private get isCustomerImpersonate() {
    return this.customer && this.customer?.isImpersonate;
  }

  private isAVerifiedPartner(user: IUserB2B) {
    return (
      user &&
      user.clientType &&
      user.clientType.b2bPartner &&
      user.isPartnerRegisterVerified
    );
  }

  private hasABrand(user: IUserB2B) {
    return user && user.brand && user.brand.name;
  }

  private hasAPartnerType(user: IUserB2B) {
    return (
      user &&
      user.advisorProfile &&
      (user.advisorProfile as IAdvisorProfile).type
    );
  }

  private isInvestorAccount() {
    return this.userB2b.isSuitabilityFinished;
  }

  private hasSwitchAccount() {
    if (
      this.userB2b &&
      this.isAVerifiedPartner(this.userB2b) &&
      this.hasABrand(this.userB2b) &&
      this.hasAPartnerType(this.userB2b) &&
      this.isInvestorAccount() &&
      !this.isPartnerWebDisabled()
    ) {
      return true;
    } else {
      return false;
    }
  }

  private isPartnerWebDisabled() {
    return FeatureFlagsModule.isEnabled(FeatureFlags.IS_PARTNER_WEB_DISABLED);
  }

  private goToCosts() {
    this.$router.push({ name: 'costs' });
    trackAmplitudeEvents.trackCostsClick();
    this.close();
  }

  private goToStatementDownload(statementType: StatementType) {
    this.$router.push({
      path: `investments/statement-download/${statementType}`
    });
    trackAmplitudeEvents.trackStatementClick(statementType);
    this.close();
  }

  private goToTradeNotesPage() {
    const currentModule = this.$route.name;
    this.$router.push({
      name: 'tradingNotes',
      query: { module: currentModule }
    });
    trackAmplitudeEvents.trackTradeNotesClick();
    this.close();
  }

  private goToIncomeReportView() {
    trackAmplitudeEvents.trackStatementClick(StatementType.IncomeStatement);

    this.goToRouteName('incomeReport');
  }

  private goToMFAPage() {
    trackAmplitudeEvents.trackMFAClick();
    this.$router.push({ name: 'security' });
  }

  onDeleteAccountClick() {
    this.deleteAccountModal = true;
    trackAmplitudeEvents.trackDeleteAccountSubmenu();
  }

  private get canShowCostsItem() {
    if (this.customer && this.customer.isRegisterVerified) return true;
    else return false;
  }

  private get appVersion() {
    const version = process.env.VUE_APP_WEB_PLATFORM_VERSION;
    return version ? `v${version}` : '';
  }

  public get isCustomerRegulatoryUpdateUpToDate() {
    return (
      this.regulatoryUpdateStatus !== RegulatoryUpdateStatus.BLOCKED &&
      this.regulatoryUpdateStatus !== RegulatoryUpdateStatus.ABOUT_TO_BE_BLOCKED
    );
  }
}
</script>

<style lang="less" scoped>
/deep/ .menu-item {
  cursor: pointer;
  background: none;
  border: none;
  width: 100%;
  padding: 0;
}

.menu_notification_title {
  .text-p-3();
  font-weight: 600;
}

.menu_my_account_title {
  .text-p-3();
  font-weight: 600;
  color: @element-primary;
}

.full-height {
  height: 100%;
}
.base-badge {
  display: block;
}
ul {
  padding: 0px;
  margin: 0px;
}

.version-item {
  display: block;
  color: @element-secondary !important;
  padding: 0 @size-spacing-x500 @size-spacing-x500;
}

.image {
  width: 56px;
  height: 56px;
  margin-left: 0px;
}
.text-wrap {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 265px;
}
.main-menu {
  /deep/ label {
    &:active {
      transform: none !important;
    }
  }
}
.navbar-container {
  background-color: @background-primary;
  border-bottom: 1px solid @divider-primary;
  margin-bottom: @size-spacing-x500;
}
.navbar-text {
  color: @element-primary;
}
.navbar-cta {
  border-radius: 0;
  margin: 0;
  padding: 0 @size-spacing-x500;
  /deep/ [class*='text-p'],
  /deep/ [class*='wrn-text'] {
    color: @element-primary;
  }
  &:hover {
    background-color: @background-hover;
  }
}
.update-config-button {
  width: 100%;
  background: @element-primary;
}

::v-deep .container-left {
  flex: 2 !important;
}

.label__brand-contact {
  color: @element-secondary;
  padding: 0px @size-spacing-x500;
}

.base-text__tag {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/deep/ .base-text {
  color: @element-primary !important;
}

.toggle__dark-mode {
  height: 28px;
}

.button_account_delete {
  display: flex;
  align-items: center;
}

.account-info {
  position: relative;
}

.account-info:first-child {
  padding: @size-spacing-x75 0 @size-spacing-x450 0;
}

.account-info:not(:first-child) {
  padding: @size-spacing-x450 0;
}

.account-info-divider::after {
  content: '';
  position: absolute;
  width: calc(100% - (@size-spacing-x500 * 2));
  bottom: 0;
  left: @size-spacing-x500;
  height: 2px;
  background: @divider-primary;
}

.servicing-info {
  cursor: pointer;

  .servicing-info__chevron {
    color: @element-secondary;
  }

  &:hover {
    background: @background-hover;
  }
}

.copy-button .text-p-5 {
  font-size: 0 !important;
}

.account-info-desc {
  padding: 0 @size-spacing-x500;
  .text-p-4 {
    color: @element-secondary;
  }

  div {
    display: flex;
    flex-direction: column;
  }

  .account-info-desc-email {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.account-info-title {
  display: flex;
  justify-content: space-between;
  padding: 0 @size-spacing-x500;

  .text-p-3 {
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
